import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useCustomerCardContext } from '../components/CustomerFormContext';
import { useCustomerCardSelector } from './useCustomerCardSelector';
import * as customerSpdSelectors from '../store/customerSpdSelectors';
import { getCustomerSpdRSAA } from '../store/customerSpdActionCreators';
import type { CustomerSpdValues } from '../types/customerSpd.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpdThunkDispatch = (action: any) => any;

/**
 * Shared hook for fetching SPD data and storing it in context's spdValues (kept distinct from
 * formValues — see CustomerFormContext). Deduplicates fetches using a single Redux status per ccid
 * (not component state, since two independent call sites can target the same ccid): 'inFlight' blocks
 * a second call site from firing a duplicate GET while the first request is still pending; 'success'
 * makes subsequent `fetchSpd()` calls no-ops; null (never attempted, or a prior attempt failed) allows
 * a fetch/retry.
 *
 * Both CustomerSpd (Show Data button) and BankInformation (eye icon reveal) use this hook.
 * Each call site gets its own `isLoading` and `isError` state for independent UI feedback.
 */
export const useGetSpd = (ccid: string | undefined) => {
    const dispatch = useDispatch() as SpdThunkDispatch;
    const { setSpdValues } = useCustomerCardContext();
    const spdStatus = useCustomerCardSelector(customerSpdSelectors.getSpdStatus(ccid ?? ''));
    const isFetched = spdStatus === 'success';
    const [isGetLoading, setIsLoading] = useState(false);
    const [isGetError, setIsError] = useState(false);

    useEffect(() => {
        if (spdStatus === 'inFlight') {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [spdStatus]);

    const fetchSpd = () => {
        if (!ccid || spdStatus === 'success' || spdStatus === 'inFlight') return;

        setIsLoading(true);
        setIsError(false);

        dispatch(
            getCustomerSpdRSAA(
                ccid,
                (values: CustomerSpdValues & { bankAccountNumber?: string | null }) => {
                    const fetchedValues: CustomerSpdValues = {
                        socialSecurityNumber: values.socialSecurityNumber ?? undefined,
                        dateOfBirth: values.dateOfBirth ?? undefined,
                        driversLicenseNumber: values.driversLicenseNumber ?? undefined,
                        driversLicenseState: values.driversLicenseState ?? null,
                        driversLicenseExpirationDate: values.driversLicenseExpirationDate ?? undefined,
                        bankAccountNumber: values.bankAccountNumber ?? undefined
                    };
                    // setSpdValues sets the SPD dirty-detection baseline to this raw fetched snapshot,
                    setSpdValues(fetchedValues);
                    setIsLoading(false);
                },
                () => {
                    setIsError(true);
                    setIsLoading(false);
                }
            )
        );
    };

    return { fetchSpd, isGetLoading, isGetError, isFetched };
};
